import React from 'react';
import { Scale, Check } from 'lucide-react';
import { useCompare } from '../context/CompareContext';
import { toast } from '../hooks/use-toast';

/**
 * A small "Compare" button. Two variants:
 *  - variant="icon": circular icon for property cards
 *  - variant="inline": text button for detail page
 */
const CompareButton = ({ propertyId, variant = 'icon', className = '' }) => {
  const { has, toggle, count, max } = useCompare();
  const active = has(propertyId);

  const onClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!active && count >= max) {
      toast({
        title: 'Compare list full',
        description: `You can compare up to ${max} properties at once. Remove one first.`,
        variant: 'destructive',
      });
      return;
    }
    const added = toggle(propertyId);
    toast({
      title: added ? 'Added to compare' : 'Removed from compare',
      description: added ? 'Pin up to 3 properties and open the Compare view.' : '',
    });
  };

  if (variant === 'inline') {
    return (
      <button
        onClick={onClick}
        data-testid={`compare-btn-inline-${propertyId}`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
          active
            ? 'bg-[#7042cc] border-[#7042cc] text-white hover:bg-[#5b35a8]'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        } ${className}`}
      >
        {active ? <Check className="h-4 w-4" /> : <Scale className="h-4 w-4" />}
        {active ? 'Pinned for compare' : 'Compare'}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      data-testid={`compare-btn-${propertyId}`}
      aria-label={active ? 'Remove from compare' : 'Add to compare'}
      className={`inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors ${
        active
          ? 'bg-[#7042cc] text-white hover:bg-[#5b35a8]'
          : 'bg-white/90 text-gray-700 hover:bg-white'
      } ${className}`}
      title={active ? 'Pinned for compare' : 'Add to compare'}
    >
      {active ? <Check className="h-4 w-4" /> : <Scale className="h-4 w-4" />}
    </button>
  );
};

export default CompareButton;
